import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound } from "lucide-react";
import { useAdminApi } from "@/hooks/useAdminApi";
import { errMsg } from "@/lib/api";
import { PartnerDocs } from "@/components/admin/PartnerDocs";

const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

const NewKey = ({ k, onClose }) => (
  <div data-testid="partner-new-key" className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 space-y-2">
    <p className="text-sm font-semibold text-amber-300">Copy this key now and send it to {k.name}. It won't be shown again.</p>
    <div className="flex gap-2">
      <code data-testid="partner-new-key-value" className="flex-1 min-w-0 break-all font-mono text-xs bg-[#08090C] border border-white/10 rounded-md p-3">{k.key}</code>
      <button data-testid="partner-new-key-copy" onClick={() => copy(k.key)} className="shrink-0 w-11 grid place-items-center rounded-md border border-white/10 hover:border-emerald-500"><Copy size={16} /></button>
    </div>
    <button data-testid="partner-new-key-done" onClick={onClose} className="text-xs text-gray-400 hover:text-white">I've saved it</button>
  </div>
);

export const PartnersTab = () => {
  const call = useAdminApi();
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const load = () => call("get", "/admin/partner-keys").then(setKeys).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const create = async (e) => {
    e.preventDefault();
    try { setFresh(await call("post", "/admin/partner-keys", { name })); setName(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke the key for ${k.name}? Their uploads will stop working immediately.`)) return;
    try { await call("delete", `/admin/partner-keys/${k.id}`); toast.success("Key revoked"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (k) => {
    try { await call("delete", `/admin/partner-keys/${k.id}`); toast.success("Removed"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-400">Partners use their personal API key to upload products straight to the store. Uploads go live immediately; existing games (same name + platform) get the stock added and the partner's price.</p>
      <form onSubmit={create} className="flex flex-col sm:flex-row gap-2">
        <input data-testid="partner-key-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Partner name"
          className="h-10 w-full sm:w-auto sm:flex-1 sm:max-w-xs px-3 rounded-md bg-[#08090C] border border-white/10 text-sm focus:outline-none focus:border-emerald-500/60" />
        <button data-testid="partner-key-create" className="h-10 px-4 rounded-md bg-emerald-500 text-[#08090C] font-bold inline-flex items-center justify-center gap-2 hover:bg-emerald-400"><KeyRound size={16} /> Create API key</button>
      </form>
      {fresh && <NewKey k={fresh} onClose={() => setFresh(null)} />}
      <div className="rounded-lg border border-white/10 bg-[#0F1117] divide-y divide-white/5">
        {keys.length === 0 && <p data-testid="partner-keys-empty" className="p-6 text-sm text-gray-500">No partner keys yet.</p>}
        {keys.map((k) => (
          <div key={k.id} data-testid={`partner-key-row-${k.id}`} className="flex items-center gap-4 p-4 text-sm">
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{k.name} {k.revoked && <span className="ml-2 text-xs text-red-400">revoked</span>}</p>
              <p className="text-xs text-gray-500 mt-1"><span className="font-mono">{k.prefix}…</span> · created {new Date(k.created_at).toLocaleDateString()} · {k.last_used_at ? `last used ${new Date(k.last_used_at).toLocaleString()}` : "never used"}</p>
            </div>
            {k.revoked
              ? <button data-testid={`partner-key-remove-${k.id}`} onClick={() => remove(k)} className="h-9 px-3 rounded-md border border-white/10 text-gray-400 hover:text-white">Remove</button>
              : <button data-testid={`partner-key-revoke-${k.id}`} onClick={() => revoke(k)} className="h-9 px-3 rounded-md border border-white/10 hover:border-red-500/60 hover:text-red-400">Revoke</button>}
          </div>
        ))}
      </div>
      <PartnerDocs />
    </div>
  );
};
